import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Search as SearchIcon, SlidersHorizontal, X, ArrowLeft, RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { CaseCard } from "../components/CaseCard";
import { VENEZUELA_STATES, SEX_OPTIONS } from "../lib/constants";

const DEFAULT_FILTERS = { case_type: "", state_region: "", sex: "", has_photo: false, status: "" };

const CHIPS = [
  { key: "all", label: "Todos", apply: {} },
  { key: "missing", label: "Desaparecidas", apply: { case_type: "missing" } },
  { key: "found", label: "Encontradas con vida", apply: { case_type: "found_alive" } },
  { key: "photo", label: "Con foto", apply: { has_photo: true } },
  { key: "recent", label: "Recientes", apply: {} },
  { key: "reunified", label: "Reunificadas", apply: { status: "reunified" } },
];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [online, setOnline] = useState(navigator.onLine);
  const [q, setQ] = useState(params.get("q") || "");
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [activeChip, setActiveChip] = useState("all");
  const [showFilters, setShowFilters] = useState(true);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE_SIZE = 12;

  const buildParams = useCallback(() => {
    const p = { limit: PAGE_SIZE };
    if (q) p.q = q;
    if (filters.case_type) p.case_type = filters.case_type;
    if (filters.state_region) p.state_region = filters.state_region;
    if (filters.sex) p.sex = filters.sex;
    if (filters.has_photo) p.has_photo = true;
    if (filters.status) p.status = filters.status;
    return p;
  }, [q, filters]);

  useEffect(() => {
    const on = () => setOnline(true); const off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  const fetchResults = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/search", { params: { ...buildParams(), page: 1 } });
      setResults(data.results);
      setTotal(data.total);
    } catch (e) {
      setResults([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  const manualRefresh = async () => { await fetchResults(); toast.success("Resultados actualizados."); };

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const nextPage = Math.floor(results.length / PAGE_SIZE) + 1;
      const { data } = await api.get("/search", { params: { ...buildParams(), page: nextPage } });
      setResults((prev) => [...prev, ...data.results]);
      setTotal(data.total);
    } catch (e) { /* noop */ }
    finally { setLoadingMore(false); }
  };

  useEffect(() => { fetchResults(); }, [fetchResults]);

  useEffect(() => {
    const onFocus = () => { if (document.visibilityState === "visible") fetchResults(); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchResults]);

  const onSubmit = (e) => {
    e.preventDefault();
    setParams(q ? { q } : {});
    fetchResults();
  };

  const applyChip = (chip) => {
    setActiveChip(chip.key);
    setFilters({ ...DEFAULT_FILTERS, ...chip.apply });
  };

  const updateFilter = (patch) => { setActiveChip(""); setFilters({ ...filters, ...patch }); };
  const clearFilters = () => { setActiveChip("all"); setFilters(DEFAULT_FILTERS); setQ(""); setParams({}); };

  return (
    <div className="space-y-5">
      <button onClick={() => navigate("/")} data-testid="search-back-btn"
        className="flex items-center gap-1.5 text-sm font-medium text-[#475569] hover:text-[#1E3A8A]">
        <ArrowLeft className="w-4 h-4" /> Volver
      </button>
      <h1 className="font-heading text-2xl font-bold text-[#0F172A]">Buscar persona</h1>

      {!online && (
        <div className="flex items-center gap-2 text-sm text-[#991B1B] bg-[#FEE2E2] border border-[#FCA5A5] rounded-lg p-3" data-testid="search-offline-note">
          No hay conexión. La búsqueda requiere internet para mostrar información actualizada.
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
            <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Busca por nombre, apellido, apodo o código de caso."
              className="w-full h-14 pl-11 pr-3 rounded-xl border border-[#E2E8F0] bg-white text-base focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]" />
          </div>
          <button type="button" onClick={() => setShowFilters(!showFilters)} data-testid="search-filters-toggle"
            className="h-14 px-4 shrink-0 rounded-xl border border-[#E2E8F0] bg-white flex items-center gap-2 text-sm font-medium text-[#475569]">
            <SlidersHorizontal className="w-5 h-5" />
            <span className="hidden sm:inline">{showFilters ? "Ocultar filtros" : "Mostrar filtros"}</span>
          </button>
        </div>

        {/* Quick filter chips */}
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="search-chips">
          {CHIPS.map((chip) => (
            <button key={chip.key} type="button" onClick={() => applyChip(chip)} data-testid={`chip-${chip.key}`}
              className={`whitespace-nowrap h-9 px-3.5 rounded-full text-sm font-medium border transition-colors ${activeChip === chip.key ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-[#475569] border-[#E2E8F0]"}`}>
              {chip.label}
            </button>
          ))}
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 rounded-xl border border-[#E2E8F0] bg-white">
            <select data-testid="filter-case-type" value={filters.case_type}
              onChange={(e) => updateFilter({ case_type: e.target.value })}
              className="h-12 px-3 rounded-lg border border-[#E2E8F0] bg-white">
              <option value="">Tipo de caso (todos)</option>
              <option value="missing">Desaparecida</option>
              <option value="found_alive">Encontrada con vida</option>
            </select>
            <select data-testid="filter-state" value={filters.state_region}
              onChange={(e) => updateFilter({ state_region: e.target.value })}
              className="h-12 px-3 rounded-lg border border-[#E2E8F0] bg-white">
              <option value="">Estado/región (todos)</option>
              {VENEZUELA_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select data-testid="filter-sex" value={filters.sex}
              onChange={(e) => updateFilter({ sex: e.target.value })}
              className="h-12 px-3 rounded-lg border border-[#E2E8F0] bg-white">
              <option value="">Género (todos)</option>
              {SEX_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <label className="flex items-center gap-2 h-12 px-3 text-sm text-[#475569]">
              <input type="checkbox" data-testid="filter-has-photo" checked={filters.has_photo}
                onChange={(e) => updateFilter({ has_photo: e.target.checked })} className="w-4 h-4" />
              Solo con foto
            </label>
          </div>
        )}

        <div className="flex gap-3">
          <button type="submit" data-testid="search-submit-btn"
            className="h-12 px-6 rounded-xl bg-[#1E3A8A] text-white font-semibold hover:bg-[#1E40AF]">
            Buscar
          </button>
          <button type="button" onClick={clearFilters} data-testid="search-clear-btn"
            className="h-12 px-4 rounded-xl bg-[#F1F5F9] text-[#475569] font-medium flex items-center gap-1.5">
            <X className="w-4 h-4" /> Limpiar filtros
          </button>
        </div>
      </form>

      <p className="text-xs text-[#94A3B8]" data-testid="search-order-note">Mostrando los casos públicos más recientes.</p>

      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-[#475569]" data-testid="search-results-count">
          {loading ? "Buscando…" : (total > results.length ? `Mostrando ${results.length} de ${total} resultados` : `${total} resultado(s)`)}
        </p>
        <button type="button" onClick={manualRefresh} disabled={loading} data-testid="search-refresh-btn"
          className="flex items-center gap-1.5 text-sm font-medium text-[#1E3A8A] disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Actualizar resultados
        </button>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
              <div className="aspect-[4/3] bg-[#F1F5F9] animate-pulse" />
              <div className="p-3 space-y-2">
                <div className="h-4 bg-[#F1F5F9] rounded animate-pulse" />
                <div className="h-3 w-2/3 bg-[#F1F5F9] rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      ) : results.length === 0 ? (
        <div className="text-center py-12 text-[#475569]" data-testid="search-empty">
          No se encontraron casos. Intenta con otros términos o limpia los filtros.
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {results.map((c) => <CaseCard key={c.id} c={c} />)}
        </div>
      )}

      {!loading && results.length < total && (
        <div className="flex justify-center pt-2">
          <button onClick={loadMore} disabled={loadingMore} data-testid="search-load-more-btn"
            className="h-12 px-8 rounded-xl bg-white border border-[#1E3A8A] text-[#1E3A8A] font-semibold disabled:opacity-50">
            {loadingMore ? "Cargando…" : `Cargar más (${total - results.length} restantes)`}
          </button>
        </div>
      )}
    </div>
  );
}
